import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { formatDate } from "../../lib/utils";
import CreateCompanyDialog from "./CreateCompanyDialog";

const KPIS = [
  { key: "tenants_total", label: "Tenants" },
  { key: "tenants_active", label: "Active" },
  { key: "tenants_suspended", label: "Suspended" },
  { key: "users_total", label: "Users" },
  { key: "tickets_total", label: "Tickets" },
];
const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-rose-50 text-rose-700 border-rose-200",
};

function renewalBadgeClass(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr) - Date.now();
  if (ms < 0) return "bg-rose-50 text-rose-700 border-rose-200";
  if (ms < 7 * 864e5) return "bg-amber-50 text-amber-700 border-amber-200";
  return null;
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = useMemo(
    () => (data?.tenants || []).filter((t) => !q || (t.name || "").toLowerCase().includes(q.toLowerCase())),
    [data?.tenants, q]
  );
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Platform</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Overview</h1>
        </div>
        <Button onClick={() => setOpenCreate(true)}>Create tenant</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {KPIS.map(({ key, label }) => (
          <div key={key} className="surface-card p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{label}</div>
            <div className="mt-2 font-display text-3xl font-bold text-slate-900 tabular-nums">
              {data?.kpis?.[key] ?? "—"}
            </div>
          </div>
        ))}
      </div>
      <div className="surface-card p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="font-medium text-slate-800">Tenants</div>
          <Input
            placeholder="Search tenants…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-tight">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-slate-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Industry</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Tickets</th>
                <th className="px-4 py-3">Renewal</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <Link to={`/admin/tenants/${t.id}`} className="text-blue-600 hover:underline font-medium">
                      {t.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
                        STATUS_TONE[t.status] || "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{t.industry}</td>
                  <td className="px-4 py-3 text-slate-500">{t.plan_name || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{t.users_count}</td>
                  <td className="px-4 py-3 tabular-nums">{t.tickets_count}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {t.plan_renewal_at ? (() => {
                      const cls = renewalBadgeClass(t.plan_renewal_at);
                      return cls
                        ? <span className={`inline-flex px-2 py-0.5 rounded-full border ${cls}`}>{formatDate(t.plan_renewal_at)}</span>
                        : <span className="text-slate-500">{formatDate(t.plan_renewal_at)}</span>;
                    })() : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-400 py-10 text-xs">No tenants.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <CreateCompanyDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
    </div>
  );
}
